// src/pages/BotConnectionPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import BotsHero from '../components/BotConnection/BotsHero';
import BotClassGrid from '../components/BotConnection/BotClassGrid';
import BotConfigCard from '../components/BotConnection/BotConfigCard';
import BotPovPanel from '../components/BotConnection/BotPovPanel';
import BotTerminal from '../components/BotConnection/BotTerminal';
import BotProTips from '../components/BotConnection/BotProTips';
import MusicControlPanel from '../components/Shared/MusicControlPanel';
import GameContainer from '../components/Shared/GameContainer';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';

import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import CaptchaModal from '../components/BotConnection/CaptchaModal';
import BotGuiModal from '../components/BotConnection/BotGuiModal';

import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import {
  WS_BOT_CONNECTION_TYPES,
  WS_BOT_CONNECTION_TOPICS,
  getBotUserQueueTopic,
  getBotAppEndpoint,
} from '../config/wsBotConnectionConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';

import './BotsPage.css';

const formatTimeStr = (date = new Date()) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `[${h}:${m}:${s}]`;
};

const BotConnectionPage = ({ openModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
  const { serverInfo, isConnecting, targetIp } = useServerConnection();
  const { addNotification, showToast } = useNotifications();

  // Target address
  const currentHost = serverInfo?.hostname || serverInfo?.ip || targetIp || '';
  const currentPort = serverInfo?.port || null;
  const fullTargetAddress = currentHost
    ? currentPort && !currentHost.includes(':')
      ? `${currentHost}:${currentPort}`
      : currentHost
    : 'mc.hypixel.net:25565';

  // State: Bot Class, Version, Nickname, Method, Skin
  const [selectedClass, setSelectedClass] = useState('op'); // 'op' | 'crash' | 'account'
  const [version, setVersion] = useState('1.20.4');
  const [username, setUsername] = useState('StormBot_47');
  const [selectedMethod, setSelectedMethod] = useState('perm_bypass');
  const [selectedSkin, setSelectedSkin] = useState('steve');

  // Deployment state
  const [isDeploying, setIsDeploying] = useState(false);
  const music = useBackgroundMusic(isDeploying);
  const [opAchieved, setOpAchieved] = useState(false);
  const [speed, setSpeed] = useState('0 p/s');
  const [packetsSent, setPacketsSent] = useState(0);
  const [sessionSeconds, setSessionSeconds] = useState(0);

  // Modals & 3D Viewer state
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [captchaData, setCaptchaData] = useState(null);
  const [isCaptchaModalOpen, setIsCaptchaModalOpen] = useState(false);
  const [viewerPort, setViewerPort] = useState(3007);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [guiData, setGuiData] = useState(null);
  const [isGuiModalOpen, setIsGuiModalOpen] = useState(false);

  // Logs state
  const [logs, setLogs] = useState(() => [
    {
      time: '[00:00:00]',
      tagType: 'system',
      text: 'Демон Bot Nexus v5.2 инициализирован. Движок готов к развертыванию.',
    },
    {
      time: '[00:00:00]',
      tagType: 'target',
      text: `Цель: ${fullTargetAddress} • Класс: OP BOT • Версия: 1.20.4 (v765).`,
    },
    {
      time: '[00:00:00]',
      tagType: 'hint',
      text: 'Нажмите «Развернуть бота» или введите команду /help в строку ниже.',
    },
  ]);

  const addLog = useCallback((tagType, text) => {
    setLogs((prev) => {
      const newLine = { time: formatTimeStr(), tagType, text };
      return [...prev, newLine].slice(-400);
    });
  }, []);

  // Update target in logs on server connect
  useEffect(() => {
    if (serverInfo) {
      addLog('target', `Целевой сервер: ${fullTargetAddress} • Соединение с сервером подтверждено.`);
    }
  }, [serverInfo, fullTargetAddress, addLog]);

  // Session ticker
  const sessionTimerRef = useRef(null);
  const streamTimerRef = useRef(null);

  useEffect(() => {
    if (isDeploying) {
      setSessionSeconds(0);
      clearInterval(sessionTimerRef.current);
      sessionTimerRef.current = setInterval(() => {
        setSessionSeconds((s) => s + 1);
      }, 1000);

      clearInterval(streamTimerRef.current);
      streamTimerRef.current = setInterval(() => {
        const currentSpeed = Math.floor(Math.random() * 400 + 1100);
        setSpeed(`${currentSpeed.toLocaleString()} p/s`);
        setPacketsSent((p) => p + Math.floor(currentSpeed / 3));
      }, 700);
    } else {
      clearInterval(sessionTimerRef.current);
      clearInterval(streamTimerRef.current);
      setSpeed('0 p/s');
    }

    return () => {
      clearInterval(sessionTimerRef.current);
      clearInterval(streamTimerRef.current);
    };
  }, [isDeploying]);

  const formatSessionTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Bot class change handler
  const handleSelectClass = (cls) => {
    setSelectedClass(cls);
    const classNames = { op: 'OP BOT', crash: 'CRASH BOT', account: 'ACCOUNT BOT' };
    addLog('target', `Класс бота изменен на: ${classNames[cls] || cls}. Загружены специализированные методы атаки.`);
    showToast(isEn ? `Class selected: ${classNames[cls] || cls}` : `Выбран класс: ${classNames[cls] || cls}`);
  };

  // Real WebSocket Subscriptions
  const subscriptions = useRef({
    botStatus: null,
    botProgress: null,
    botError: null,
    botGui: null,
  });

  const handleResetView = useCallback(() => {
    setIsDeploying(false);
    setIsCaptchaModalOpen(false);
    setOpAchieved(false);
  }, []);

  const handleBotStatusMessage = useCallback(
    (data, wsError) => {
      if (wsError || data?.type === WS_BOT_CONNECTION_TYPES.ERROR) {
        const errorMsg = data?.message || wsError?.message || 'An unknown bot operation error occurred.';
        setIsDeploying(false);
        setIsCaptchaModalOpen(false);
        addLog('warn', `Bot Error: ${errorMsg}`);
        if (data?.isFatal) handleResetView();
        return;
      }

      switch (data?.type) {
        case WS_BOT_CONNECTION_TYPES.DEPLOYMENT_STARTED:
          setIsDeploying(true);
          setOpAchieved(false);
          addLog('system', data.message || `Развертывание ${data.botType || 'бота'} запущено`);
          showToast(isEn ? 'Bot deployment started' : 'Развертывание бота запущено');
          break;

        case WS_BOT_CONNECTION_TYPES.DEPLOYMENT_COMPLETED: {
          setIsDeploying(false);
          setIsCaptchaModalOpen(false);
          const success = data.success ?? false;
          const msg = data.message || (success ? 'Развертывание успешно завершено.' : 'Развертывание остановлено.');
          addLog(success ? 'success' : 'warn', msg);
          showToast(msg);

          if (success && selectedClass === 'op' && data.resultData?.opAchieved) {
            setOpAchieved(true);
            addLog('success', 'Права оператора успешно подтверждены!');
          }
          if (success && selectedClass === 'account' && data.resultData?.password) {
            addLog('success', `Пароль аккаунта найден: ${data.resultData.password}`);
          }

          window.dispatchEvent(new CustomEvent('showAiInvite', {
            detail: { message: t('aiAssistantInviteBots') }
          }));
          break;
        }

        case WS_BOT_CONNECTION_TYPES.CAPTCHA_REQUIRED: {
          const payload = data?.data || data;
          setCaptchaData(payload);
          setIsCaptchaModalOpen(true);
          addLog('warn', `[Captcha] Требуется решение капчи 3x3 для ${payload?.botName || 'бота'}`);
          showToast(isEn ? 'Captcha verification required' : 'Требуется проверка капчи');
          break;
        }

        case WS_BOT_CONNECTION_TYPES.VIEWER_READY: {
          const port = data?.port || data?.data?.port;
          if (port) {
            setViewerPort(port);
            setIsViewerReady(true);
            addLog('success', `[Viewer] 3D Live Stream активен на порту ${port}`);
          }
          break;
        }

        case WS_BOT_CONNECTION_TYPES.GUI_WINDOW_OPEN: {
          const payload = data?.data || data;
          setGuiData(payload);
          setIsGuiModalOpen(true);
          addLog('bot', `[GUI] Открыто меню "${payload?.title || 'Menu'}" (${payload?.slots?.length || 0} слотов)`);
          break;
        }

        case WS_BOT_CONNECTION_TYPES.GUI_WINDOW_CLOSE:
          setIsGuiModalOpen(false);
          setGuiData(null);
          break;

        default:
          break;
      }
    },
    [addLog, handleResetView, selectedClass, showToast, isEn, t]
  );

  const handleBotProgressMessage = useCallback(
    (message, wsError) => {
      const type = message?.type;
      const data = message?.data;
      if (wsError) {
        addLog('warn', `Progress Error: ${wsError.message}`);
        return;
      }
      if (type === WS_BOT_CONNECTION_TYPES.DEPLOYMENT_PROGRESS && data?.line) {
        addLog(data.line.type === 'error' ? 'warn' : data.line.type === 'success' ? 'success' : 'packet', data.line.text);
      } else if (type === WS_BOT_CONNECTION_TYPES.CONSOLE_LINE && data?.line) {
        addLog(data.line.type === 'error' ? 'warn' : data.line.type === 'success' ? 'success' : 'packet', data.line.text);
      }
    },
    [addLog]
  );

  const handleBotErrorMessage = useCallback(
    (data, wsError) => {
      const errorMsg = handleWebSocketError(data, wsError, openModal, addNotification, {
        defaultTitle: t('botConnectionModule'),
        moduleName: t('bots'),
      });
      setIsDeploying(false);
      setIsCaptchaModalOpen(false);
      addLog('warn', `Ошибка: ${errorMsg}`);
      if (data?.isFatal) handleResetView();
    },
    [addLog, handleResetView, t, openModal, addNotification]
  );

  // Subscribe to backend topics when connected
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) return;

    const cleanup = () => {
      Object.values(subscriptions.current).forEach((unsub) => unsub?.());
      subscriptions.current = { botStatus: null, botProgress: null, botError: null, botGui: null };
    };

    cleanup();

    if (serverInfo) {
      const botStatusTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.STATUS;
      const botProgressTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.PROGRESS;
      const botErrorTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.ERROR;
      const botGuiTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.USER_GUI;

      subscriptions.current.botStatus = subscribe(botStatusTopic, handleBotStatusMessage);
      subscriptions.current.botProgress = subscribe(botProgressTopic, handleBotProgressMessage);
      subscriptions.current.botError = subscribe(botErrorTopic, handleBotErrorMessage);
      subscriptions.current.botGui = subscribe(botGuiTopic, (message) => {
        if (message?.type === WS_BOT_CONNECTION_TYPES.GUI_WINDOW_OPEN && message?.data) {
          setGuiData(message.data);
          setIsGuiModalOpen(true);
        } else if (message?.type === WS_BOT_CONNECTION_TYPES.GUI_WINDOW_CLOSE) {
          setIsGuiModalOpen(false);
          setGuiData(null);
        }
      });
    }

    return () => cleanup();
  }, [
    isWebSocketConnected,
    isAuthenticated,
    serverInfo,
    subscribe,
    handleBotStatusMessage,
    handleBotProgressMessage,
    handleBotErrorMessage,
  ]);

  // Execute real bot deployment
  const startRealDeployment = useCallback(() => {
    const rawAddress = targetIp || serverInfo?.hostname || serverInfo?.ip || '';
    const targetAddress = rawAddress.replace(/^https?:\/\//i, '').replace(/\/+$/, '').trim();

    const botTypeBackend = `${selectedClass}-bot`; // 'op-bot', 'crash-bot', 'account-bot'

    setIsDeploying(true);
    setIsViewerReady(false);
    addLog('system', `Запуск клиента [${username}]...`);
    addLog('bot', `Установка TCP-сокета к ${targetAddress || fullTargetAddress} через Netty pipeline...`);

    sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/deploy', {
      ip: targetAddress || fullTargetAddress,
      botType: botTypeBackend,
      config: {
        botUsername: username,
        attackMethod: selectedMethod,
        version: version,
        botSkin: selectedSkin,
        botBehavior: 'stealth',
        numAttempts: 100,
      },
    });

    showToast(isEn ? 'Bot deployment started' : 'Бот успешно развернут на сервере');
  }, [
    targetIp,
    serverInfo,
    selectedClass,
    username,
    selectedMethod,
    version,
    selectedSkin,
    fullTargetAddress,
    sendMessage,
    addLog,
    showToast,
    isEn,
  ]);

  const stopRealDeployment = useCallback(() => {
    setIsDeploying(false);
    setIsViewerReady(false);
    sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/stop', { ip: serverInfo?.ip || fullTargetAddress });
    addLog('warn', `Сессия клиента ${username} завершена.`);
    showToast(isEn ? 'Bot session stopped' : 'Сессия бота остановлена');
    window.dispatchEvent(new CustomEvent('showAiInvite', {
      detail: { message: t('aiAssistantInviteBots') }
    }));
  }, [serverInfo, fullTargetAddress, sendMessage, username, addLog, showToast, isEn, t]);

  const handleToggleDeployment = () => {
    if (!isDeploying) {
      if (!serverInfo) {
        showToast(isEn ? 'Please specify and audit target server first' : 'Сначала укажите целевой сервер');
        return;
      }
      setPendingAction('deploy');
      setIsTermsModalOpen(true);
    } else {
      stopRealDeployment();
    }
  };

  // Bot Movement & Look
  const handleSendControl = useCallback(
    (key, state) => {
      if (!isWebSocketConnected) return;
      sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_CONTROL), { key, state });
    },
    [isWebSocketConnected, sendMessage]
  );

  const handleSendLook = useCallback(
    (yawRad, pitchRad) => {
      if (!isWebSocketConnected) return;
      sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_LOOK), { yaw: yawRad, pitch: pitchRad });
    },
    [isWebSocketConnected, sendMessage]
  );

  // GUI Menu / Slots
  const handleSlotClick = useCallback(
    (windowId, slot) => {
      if (!isWebSocketConnected) return;
      sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_GUI_CLICK), {
        windowId,
        slot,
        mouseButton: 0,
        mode: 0,
      });
      addLog('bot', `[GUI] Клик по слоту #${slot} в окне #${windowId}`);
    },
    [isWebSocketConnected, sendMessage, addLog]
  );

  const handleCloseGui = useCallback(() => {
    setIsGuiModalOpen(false);
    if (isWebSocketConnected) {
      sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_GUI_CLOSE), {});
    }
  }, [isWebSocketConnected, sendMessage]);

  const handleOpenMenu = useCallback(() => {
    if (!isWebSocketConnected) return;
    sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_GUI_OPEN_MENU), {});
    addLog('bot', '[Menu] Запрос активации меню сервера / компаса...');
  }, [isWebSocketConnected, sendMessage, addLog]);

  // Captcha Interaction
  const handleInteractCaptchaTile = useCallback(
    (entityId, index) => {
      if (sendMessage) {
        sendMessage(getBotAppEndpoint('/interact-captcha'), {
          type: WS_BOT_CONNECTION_TYPES.INTERACT_CAPTCHA,
          entityId,
          index,
          botName: captchaData?.botName,
        });
        addLog('bot', `Поворот рамки капчи #${index + 1} для ${captchaData?.botName || 'бота'}`);
      }
    },
    [sendMessage, captchaData, addLog]
  );

  // Interactive Shell Command Execution
  const handleExecuteCommand = (cmd) => {
    addLog('bot', `Выполнение команды: ${cmd}`);
    const lower = cmd.toLowerCase();

    if (lower === '/help') {
      addLog('system', 'Доступные команды виртуального клиента Bot Nexus:');
      addLog('system', '  /say <текст>        - Отправить сообщение в чат сервера');
      addLog('system', '  /attack [start|stop] - Запустить или остановить сценарий атаки');
      addLog('system', '  /jump               - Совершить прыжок в мире');
      addLog('system', '  /sneak              - Присесть / встать');
      addLog('system', '  /tp <x> <y> <z>     - Переместить бота по координатам');
      addLog('system', '  /skin <name>        - Сменить скин бота (steve/alex/hacker/cyber/tuxedo)');
      addLog('system', '  /status             - Показать статус сокета и пинг');
      addLog('system', '  /clear              - Очистить лог консоли');
      addLog('system', '  /op check           - Проверить наличие прав оператора');
      addLog('system', '  /crash test         - Запустить стресс-тест пакетов');
    } else if (lower.startsWith('/say ')) {
      const msg = cmd.substring(5);
      addLog('packet', `[CHAT OUT] <${username}>: ${msg}`);
      if (serverInfo && isWebSocketConnected) {
        sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/command', {
          ip: serverInfo.ip,
          command: msg,
        });
      }
      showToast(`Отправлено: "${msg}"`);
    } else if (lower === '/attack start') {
      if (!isDeploying) startRealDeployment();
      addLog('success', 'Сценарий атаки активирован в основном потоке.');
    } else if (lower === '/attack stop') {
      if (isDeploying) stopRealDeployment();
      addLog('warn', 'Сценарий атаки остановлен.');
    } else if (lower === '/clear') {
      setLogs([]);
      showToast(isEn ? 'Console cleared' : 'Консоль очищена');
    } else if (lower === '/status') {
      addLog(
        'system',
        `Статус: ${isDeploying ? 'ONLINE (Развернут)' : 'OFFLINE (IDLE)'} | Сервер: ${fullTargetAddress} | Пакеты: ${packetsSent}`
      );
    } else if (lower === '/op check') {
      addLog('packet', 'Запрос Permissions Level: проверка прав /op...');
      setTimeout(() => {
        addLog('warn', 'Permission Level: 0 (Игрок). Запустите метод Perm-Bypass для эскалации.');
      }, 400);
    } else if (lower === '/crash test') {
      addLog('packet', 'Запуск пакетного краш-теста инвентаря (Packet 0x0E flood)...');
      setTimeout(() => {
        addLog('warn', 'Целевой сервер: получено 120 пакетов за 1 тик. TPS снизился до 17.8.');
      }, 600);
    } else {
      // Forward directly to backend if connected
      if (serverInfo && isWebSocketConnected) {
        sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/command', {
          ip: serverInfo.ip,
          command: cmd,
        });
      }
    }
  };

  const handleClearLogs = () => {
    setLogs([
      {
        time: formatTimeStr(),
        tagType: 'system',
        text: 'Журнал консоли очищен пользователем.',
      },
    ]);
    showToast(isEn ? 'Console cleared' : 'Консоль очищена');
  };

  const handleCopyLogs = () => {
    const text = logs.map((l) => `${l.time} [${l.tagType.toUpperCase()}] ${l.text}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      showToast(isEn ? 'Console logs copied' : 'Журнал консоли скопирован');
    });
  };

  const promptHost = useMemo(() => {
    if (!fullTargetAddress) return 'server';
    const hostPart = fullTargetAddress.split(':')[0];
    return hostPart.split('.')[0] || 'server';
  }, [fullTargetAddress]);

  const isDisabled = !serverInfo || isConnecting || isAuthLoading;

  return (
    <PageTransition>
      <div className="bots-page-container min-h-screen text-white relative font-sans">
        <div className="relative z-10 max-w-[1320px] mx-auto px-3 sm:px-6 pt-6 sm:pt-14">
          {/* 1. Hero: Bebas Neue BOT NEXUS */}
          <BotsHero />

          {/* 2. Target Server MOTD Card */}
          <ServerInfoCard className="w-full mx-auto relative z-40 mb-4 sm:mb-[30px]" />

          {/* 3. Bot Class Grid (OP BOT, CRASH BOT, ACCOUNT BOT) */}
          <BotClassGrid
            selectedClass={selectedClass}
            onSelectClass={handleSelectClass}
            disabled={isDeploying}
          />

          {/* 4. Session Configuration */}
          <BotConfigCard
            selectedClass={selectedClass}
            version={version}
            setVersion={setVersion}
            username={username}
            setUsername={setUsername}
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
            selectedSkin={selectedSkin}
            setSelectedSkin={setSelectedSkin}
            isDeploying={isDeploying}
            isDisabled={isDisabled}
            onToggleDeployment={handleToggleDeployment}
            onShowToast={showToast}
            onAddLog={addLog}
          />

          {/* 5. POV Viewport & HUD */}
          <BotPovPanel
            isDeploying={isDeploying}
            selectedClass={selectedClass}
            username={username}
            targetServer={fullTargetAddress}
            viewerPort={viewerPort}
            isViewerReady={isViewerReady}
            onSendControl={handleSendControl}
            onSendLook={handleSendLook}
            onOpenMenu={handleOpenMenu}
            onAddLog={addLog}
          />

          {/* Music Control Panel */}
          <MusicControlPanel
            isPlaying={isDeploying}
            volume={music.volume}
            setVolume={music.setVolume}
            isMuted={music.isMuted}
            setIsMuted={music.setIsMuted}
            currentTime={music.currentTime}
            currentTrack={music.currentTrack}
            lyrics={music.lyrics}
            analyser={music.analyser}
            accentColor="blue"
            className="mb-4"
            onNextTrack={music.nextTrack}
          />

          {/* Mini-Games Panel */}
          <GameContainer
            isActive={isDeploying}
            accentColor="cyan"
            className="mb-8"
          />

          {/* 6. Bot Log & Network Console Terminal */}
          <BotTerminal
            logs={logs}
            speed={speed}
            statusText={isDeploying ? 'РАЗВЕРНУТ (ACTIVE)' : 'IDLE (Ожидание)'}
            statusColor={isDeploying ? '#2ed573' : '#ffffff'}
            packetsSent={packetsSent}
            sessionTime={formatSessionTime(sessionSeconds)}
            promptHost={promptHost}
            onClearLogs={handleClearLogs}
            onCopyLogs={handleCopyLogs}
            onExecuteCommand={handleExecuteCommand}
          />

          {/* 7. Expert Recommendations • Pro Tips */}
          <BotProTips />
        </div>

        {/* Modals */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          onAccept={() => {
            setIsTermsModalOpen(false);
            if (pendingAction === 'deploy') startRealDeployment();
            setPendingAction(null);
          }}
          onDecline={() => {
            setIsTermsModalOpen(false);
            setPendingAction(null);
          }}
          t={t}
        />

        <CaptchaModal
          isOpen={isCaptchaModalOpen}
          captchaData={captchaData}
          onInteractTile={handleInteractCaptchaTile}
          onSkip={() => setIsCaptchaModalOpen(false)}
          onClose={() => setIsCaptchaModalOpen(false)}
        />

        <BotGuiModal
          guiData={guiData}
          onSlotClick={handleSlotClick}
          onClose={handleCloseGui}
        />
      </div>
    </PageTransition>
  );
};

export default BotConnectionPage;